
import React, { useState, useEffect, useCallback } from "react";
import {
  Card,
  CardHeader,
  Table,
  Nav,
  NavItem,
  NavLink,
  Button,
  ButtonGroup,
  Input,
  Form,
  FormGroup,
  Label,
  Container,
  Row,
  Col,
} from "reactstrap";
import classnames from "classnames";
import axiosInstance from "../request";
import { URL } from "../api";

const Blocked = () => {
  const [activeTab, setActiveTab] = useState("state");
  const [blockData, setBlockData] = useState([]);
  const [filter, setFilter] = useState("all");
  const [showAddForm, setShowAddForm] = useState(false);
  const [newEntry, setNewEntry] = useState({});

  // Toggle tabs
  const toggleTab = (tab) => {
    if (activeTab !== tab) {
      setActiveTab(tab);
      setFilter("all");
      setShowAddForm(false);
      setNewEntry({});
    }
  };

  // Get API URLs
  const getApiUrl = useCallback(() => `${URL.BLOCKED_AREA}/${activeTab}`, [activeTab]);
  const getAddUrl = () => activeTab === "city" ? `${URL.BLOCKED_AREA}/cityAdd` : "";
  const getUpdateUrl = () => `${URL.BLOCKED_AREA}/${activeTab}Update`;

  // Fetch blocked data
  const fetchBlockData = useCallback(async () => {
    try {
      const response = await axiosInstance.get(getApiUrl());
      let data = response.data;

      // Sort states alphabetically
      if (activeTab === "state") {
        data.sort((a, b) => a.stateName.localeCompare(b.stateName));
      }

      setBlockData(data);
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }, [getApiUrl, activeTab]);

  // Update blocked status
  const handleStatusChange = async (id, newStatus) => {
    try {
      const item = blockData.find((entry) => entry.id === id);
      if (!item) return;

      const payload = activeTab === "state"
        ? { id, stateName: item.stateName, stateCode: item.stateCode, isBlocked: newStatus }
        : { id, district: item.district, state: item.state, stateCode: item.stateCode, cibilStateCode: item.cibilStateCode, isBlocked: newStatus };

      await axiosInstance.post(`${getUpdateUrl()}?id=${id}`, payload, {
        headers: { "Content-Type": "application/json" },
      });

      setBlockData(prev => prev.map(entry => entry.id === id ? { ...entry, isBlocked: newStatus } : entry));
    } catch (error) {
      console.error("Update failed:", error);
      alert("Failed to update status.");
    }
  };

  // Add new city
  const handleAddNew = async (e) => {
    e.preventDefault();
    try {
      const response = await axiosInstance.post(getAddUrl(), newEntry, {
        headers: { "Content-Type": "application/json" },
      });

      setBlockData(prev => [response.data, ...prev]);
      setShowAddForm(false);
      setNewEntry({});
    } catch (err) {
      console.error("Add failed:", err);
      alert("Failed to add entry.");
    }
  };

  // Filter data
  const getFilteredData = () => {
    if (filter === "blocked") return blockData.filter(item => item.isBlocked === 1);
    if (filter === "unblocked") return blockData.filter(item => item.isBlocked === 0);
    return blockData;
  };

  useEffect(() => {
    fetchBlockData();
  }, [fetchBlockData, activeTab]);

  return (
    <Container className="mt-5" fluid>
      <Row>
        <Col xl="12">
          <Card className="shadow">
            <CardHeader className="border-0 mx-2">
              <h3>Blocked Areas</h3>
            </CardHeader>

            {/* Tabs */}
            <Nav tabs className="pl-4">
              {["state", "city"].map(tab => (
                <NavItem key={tab}>
                  <NavLink
                    className={classnames({ active: activeTab === tab })}
                    onClick={() => toggleTab(tab)}
                  >
                    {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  </NavLink>
                </NavItem>
              ))}
            </Nav>

            {/* Controls */}
            <div className="d-flex justify-content-end align-items-center mt-3 gap-2 flex-wrap">
              <ButtonGroup size="sm" className="gap-1">
                {["all", "blocked", "unblocked"].map(f => (
                  <Button
                    key={f}
                    color={filter === f ? "primary" : "secondary"}
                    onClick={() => setFilter(f)}
                  >
                    {f.charAt(0).toUpperCase() + f.slice(1)}
                  </Button>
                ))}
              </ButtonGroup>

              {activeTab === "city" && (
                <Button size="sm" color="info" onClick={() => setShowAddForm(!showAddForm)}>
                  {showAddForm ? "Cancel" : "Add New"}
                </Button>
              )}
            </div>

            {/* Add City Form */}
            {showAddForm && activeTab === "city" && (
              <Form onSubmit={handleAddNew} className="mt-3 border p-3 rounded bg-light">
                {["district", "state", "stateCode"].map(field => (
                  <FormGroup key={field}>
                    <Label>{field.charAt(0).toUpperCase() + field.slice(1)}</Label>
                    <Input
                      required
                      type={field === "stateCode" ? "number" : "text"}
                      value={newEntry[field] || ""}
                      onChange={e => setNewEntry({ ...newEntry, [field]: field === "stateCode" ? parseInt(e.target.value) || "" : e.target.value })}
                    />
                  </FormGroup>
                ))}

                <FormGroup>
                  <Label>Status</Label>
                  <Input
                    type="select"
                    value={newEntry.isBlocked ?? 0}
                    onChange={e => setNewEntry({ ...newEntry, isBlocked: parseInt(e.target.value) })}
                  >
                    <option value={0}>Active</option>
                    <option value={1}>Blocked</option>
                  </Input>
                </FormGroup>

                <Button color="success" type="submit">Submit</Button>
              </Form>
            )}

            {/* Table */}
            <Table bordered responsive className="mt-3">
              <thead>
                <tr>
                  {activeTab === "city" && <th>District</th>}
                  <th>State</th>
                  {activeTab === "state" && <th>State Code</th>}
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {getFilteredData().map(item => (
                  <tr key={item.id}>
                    {activeTab === "city" && <td>{item.district}</td>}
                    <td>{item.state || item.stateName}</td>
                    {activeTab === "state" && <td>{item.stateCode}</td>}
                    <td>
                      <Input
                        type="select"
                        bsSize="sm"
                        value={item.isBlocked === 1 ? 1 : 0}
                        onChange={e => handleStatusChange(item.id, parseInt(e.target.value))}
                      >
                        <option value={1}>Blocked</option>
                        <option value={0}>Active</option>
                      </Input>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default Blocked;
